import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Basic validation
    if (!formData.company || !formData.name || !formData.email || !formData.phone || !formData.message) {
      return;
    }

    if (formData.message.length > 500) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('company', formData.company);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d645gtqbdoed7v0v9100', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: '본사 주소',
      content: '경기 시흥시 은계남길 32 성강해오름 지식산업센터 7층 705호',
      color: 'from-teal-500 to-teal-600'
    },
    {
      icon: 'ri-phone-line',
      title: '사무실 전화',
      content: '031-313-2663',
      color: 'from-teal-500 to-teal-600'
    },
    {
      icon: 'ri-smartphone-line',
      title: '휴대폰',
      content: '010-3319-6611',
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      icon: 'ri-mail-line',
      title: '이메일',
      content: 'tel9060@gmail.com',
      color: 'from-teal-600 to-cyan-600'
    },
    {
      icon: 'ri-printer-line',
      title: '팩스',
      content: '0504-229-7701',
      color: 'from-teal-600 to-teal-700'
    },
    {
      icon: 'ri-time-line',
      title: '운영 시간',
      content: '평일 09:00 - 18:00',
      color: 'from-cyan-500 to-teal-600'
    }
  ];

  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-block px-5 py-2 bg-teal-50 border border-teal-200 rounded-full mb-4">
            <span className="text-teal-600 text-xs md:text-sm font-bold uppercase tracking-wider">Contact Us</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4 md:mb-6">
            문의하기
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto font-medium">
            로보에셋그리드에 대해 궁금하신 점이 있으시면 언제든지 연락주세요
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          {/* Contact Information */}
          <div className="space-y-6 md:space-y-8">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-200">
              <h3 className="text-xl md:text-3xl font-black text-gray-900 mb-6 md:mb-8">
                연락처 정보
              </h3>

              <div className="space-y-5 md:space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4 md:gap-5">
                    <div
                      className={`w-12 md:w-14 h-12 md:h-14 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center flex-shrink-0`}
                    >
                      <i className={`${info.icon} text-xl md:text-2xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">
                        {info.title}
                      </h4>
                      <p className="text-sm md:text-lg text-gray-700 font-medium break-all">
                        {info.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Google Map */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
              <div className="w-full h-[300px] md:h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3174.8!2d126.8028!3d37.3796!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDIyJzQ2LjYiTiAxMjbCsDQ4JzEwLjEiRQ!5e0!3m2!1sko!2skr!4v1700000000000!5m2!1sko!2skr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="로보에셋그리드 위치"
                ></iframe>
              </div>
              <div className="p-4 md:p-5 bg-gray-50">
                <p className="text-sm md:text-base text-gray-700 font-bold text-center">
                  <i className="ri-map-pin-line mr-2 text-teal-600"></i>
                  경기 시흥시 은계남길 32 성강해오름 지식산업센터 7층 705호
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-200">
            <h3 className="text-xl md:text-3xl font-black text-gray-900 mb-6 md:mb-8">
              문의 양식
            </h3>

            <form id="contact-form" onSubmit={handleSubmit} className="space-y-5 md:space-y-6" data-readdy-form>
              <div>
                <label htmlFor="name" className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="담당자명을 입력하세요"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2">
                  이메일 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="example@company.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2">
                  연락처 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="010-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2">
                  회사명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="회사명을 입력하세요"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm md:text-base font-bold text-gray-700 mb-1.5 md:mb-2">
                  문의 내용 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 text-sm resize-none"
                  placeholder="문의하실 내용을 입력하세요 (최대 500자)"
                ></textarea>
                <div className="text-right text-[10px] md:text-xs text-gray-500 mt-1">
                  {formData.message.length} / 500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 md:py-4 px-5 md:px-6 rounded-xl font-bold text-base md:text-lg hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '전송 중...' : '문의하기'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-3 md:px-4 py-2.5 md:py-3 rounded-lg text-xs md:text-sm">
                  문의가 성공적으로 전송되었습니다. 빠른 시일 내에 답변드리겠습니다.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 md:px-4 py-2.5 md:py-3 rounded-lg text-xs md:text-sm">
                  문의 전송에 실패했습니다. 다시 시도해 주세요.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
